/**
 * Column order and fallback widths for the two delivery tables.
 *
 * The expanded history rows are a CSS grid laid over the parent table, so they
 * must render exactly one cell per column here or every history row shifts
 * sideways. Keeping the order in one place lets a test hold the two in step
 * instead of a comment asking the next person to remember.
 *
 * Widths are the fallback used before the parent table is measured, or if
 * measurement fails.
 */

const SELECTION_COLUMN_WIDTH = 48
export const EXPAND_COLUMN_WIDTH = 48
/*
 * Sized to what each one-line title and its widest cell actually need, so the
 * payment table (the wider of the two) fits a ~1600px window without hiding
 * Drawn Status or Actions behind the horizontal scroll.
 */
const COMPANY_COLUMN_WIDTH = 230
const TEMPLATE_COLUMN_WIDTH = 150
const STATUS_COLUMN_WIDTH = 125
const FILE_COLUMN_WIDTH = 230
const DUE_DATE_COLUMN_WIDTH = 130
const UPLOADED_COLUMN_WIDTH = 190
const ACTIONS_COLUMN_WIDTH = 170

/** [selection, expand, Company, Email template, File, Status, Due date, Uploaded, Actions] */
export const DELIVERY_COLUMN_WIDTHS: readonly number[] = [
  SELECTION_COLUMN_WIDTH,
  EXPAND_COLUMN_WIDTH,
  COMPANY_COLUMN_WIDTH,
  TEMPLATE_COLUMN_WIDTH,
  FILE_COLUMN_WIDTH,
  STATUS_COLUMN_WIDTH,
  DUE_DATE_COLUMN_WIDTH,
  UPLOADED_COLUMN_WIDTH,
  ACTIONS_COLUMN_WIDTH,
]

const PAYMENT_ACTIONS_COLUMN_WIDTH = 232
const PAYMENT_METHOD_COLUMN_WIDTH = 158
const AMOUNT_COLUMN_WIDTH = 105
const PAYMENT_DUE_DATE_COLUMN_WIDTH = 130
const ACTUAL_AMOUNT_COLUMN_WIDTH = 150
const DRAW_STATUS_COLUMN_WIDTH = 175
// Narrower than the plain delivery table's File column: the payment columns
// need the room, and the file name keeps its ending visible anyway. The date
// stays on one line; the uploader email wraps under it.
const PAYMENT_FILE_COLUMN_WIDTH = 195

/**
 * [selection, expand, Company, Email template, File, Email Status, Payment
 * Method, Amount, Due date, Actual Amount, Drawn Status, Actions]. Uploaded is
 * merged into File here, and the payment columns are additions.
 */
export const DELIVERY_WITH_PAYMENT_COLUMN_WIDTHS: readonly number[] = [
  SELECTION_COLUMN_WIDTH,
  EXPAND_COLUMN_WIDTH,
  COMPANY_COLUMN_WIDTH,
  TEMPLATE_COLUMN_WIDTH,
  PAYMENT_FILE_COLUMN_WIDTH,
  STATUS_COLUMN_WIDTH,
  PAYMENT_METHOD_COLUMN_WIDTH,
  AMOUNT_COLUMN_WIDTH,
  PAYMENT_DUE_DATE_COLUMN_WIDTH,
  ACTUAL_AMOUNT_COLUMN_WIDTH,
  DRAW_STATUS_COLUMN_WIDTH,
  PAYMENT_ACTIONS_COLUMN_WIDTH,
]

export const DELIVERY_COLUMN_WIDTH_BY_TITLE = {
  company: COMPANY_COLUMN_WIDTH,
  template: TEMPLATE_COLUMN_WIDTH,
  status: STATUS_COLUMN_WIDTH,
  file: FILE_COLUMN_WIDTH,
  dueDate: DUE_DATE_COLUMN_WIDTH,
  uploaded: UPLOADED_COLUMN_WIDTH,
  actions: ACTIONS_COLUMN_WIDTH,
} as const

export const DELIVERY_WITH_PAYMENT_COLUMN_WIDTH_BY_TITLE = {
  company: COMPANY_COLUMN_WIDTH,
  template: TEMPLATE_COLUMN_WIDTH,
  status: STATUS_COLUMN_WIDTH,
  file: PAYMENT_FILE_COLUMN_WIDTH,
  paymentMethod: PAYMENT_METHOD_COLUMN_WIDTH,
  amount: AMOUNT_COLUMN_WIDTH,
  dueDate: PAYMENT_DUE_DATE_COLUMN_WIDTH,
  actualAmount: ACTUAL_AMOUNT_COLUMN_WIDTH,
  drawStatus: DRAW_STATUS_COLUMN_WIDTH,
  actions: PAYMENT_ACTIONS_COLUMN_WIDTH,
} as const
